import { Meal } from "@/clients/models/Meal";
import { StackedModal } from "@/components/modal/stacked-modal";
import { NORMALISED_CARB_SWAP_SET } from "@/features/meal-plan/constants/carb-swap";
import { useGetScaledServings } from "@/hooks/requests/meal/use-get-scaled-servings";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { memo, useMemo, useState } from "react";
import { CarbSwapTag } from "../meal-detail-tags/carb-swap-tag";
import { IngredientsServingSelector } from "./ingredients-serving-selector";
import { PlanDetailBottomSheetHeader } from "./plan-detail-bottom-sheet-header";

interface IngredientsModalProps {
  pageRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
  onDismiss: () => void;
  meal: Meal;
  mealDay?: string;
  targetDate?: string;
  fromPlan?: boolean;
}

/**
 * Ingredients modal displaying meal ingredients in a bottom sheet
 * Based on Figma design with purple gradient header
 */
export const IngredientsModal = memo(function IngredientsModal({
  pageRef,
  isOpen,
  onDismiss,
  meal,
  mealDay,
  targetDate,
  fromPlan,
}: IngredientsModalProps) {
  const isCarbSwap = useMemo(() => {
    console.log("meal.categoryPills", meal.categoryPills);
    return meal.categoryPills?.some((pill) =>
      NORMALISED_CARB_SWAP_SET.has(pill.toLowerCase().trim()),
    );
  }, [meal.categoryPills]);
  console.log("isCarbSwap", isCarbSwap);
  const [servings, setServings] = useState(1);

  const { scaledIngredients, isLoading } = useGetScaledServings({
    mealId: meal.id ?? "",
    servingSize: servings,
    enabled: isOpen && !!meal.id,
    source: fromPlan ? "plan" : "latest",
    targetDate,
  });

  // Use scaled ingredients if available, otherwise fall back to original ingredients
  const displayIngredients = useMemo(() => {
    return scaledIngredients.length > 0 ? scaledIngredients : meal.ingredients;
  }, [scaledIngredients, meal.ingredients]);

  return (
    <StackedModal
      pageRef={pageRef}
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      showHeader={false}
      classNames={{ content: "overflow-y-hidden" }}
      forceOverscroll={false}
    >
      <div className="relative flex h-full flex-col overflow-hidden">
        <div
          className={cn(
            "pointer-events-none absolute -top-1 -right-1 -left-1 h-[251px]",
            "to-screen-gs-1 bg-gradient-to-t from-[rgba(96,65,138,0)]",
          )}
        />
        <PlanDetailBottomSheetHeader title="Ingredients" onDismiss={onDismiss} />
        <div className="flex flex-1 flex-col overflow-y-auto">
          {meal.description && meal.description.length > 0 && (
            <p className={cn(textVariants.bodySmall, "relative z-10 pb-8 text-center text-white")}>
              {meal.description}
            </p>
          )}

          <div className="relative z-10 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 pb-8">
            {meal.productComplexity?.toLowerCase() === "composite" && (
              <IngredientsServingSelector
                mealDay={mealDay}
                servings={servings}
                onServingsChange={setServings}
              />
            )}

            <div className="relative z-10 flex flex-1 flex-col gap-3 px-3">
              {isLoading ? (
                <>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <IonSkeletonText key={i} animated className="h-6 w-full rounded-xl" />
                  ))}
                </>
              ) : (
                displayIngredients.map((ingredient, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <p className={cn(textVariants.title3, "!font-medium")}>{ingredient}</p>
                  </div>
                ))
              )}
            </div>
          </div>
          {isCarbSwap && (
            <div className="relative z-10 flex justify-center pb-8">
              <CarbSwapTag label="Carb Swap" className="border border-[#FFC3004D]/30" />
            </div>
          )}
        </div>
      </div>
    </StackedModal>
  );
});
